import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Pdf } from "../components/Pdf";

/**
 * Dokumentet i full skjerm.
 *
 * ÉN GEOMETRI, IKKE TO. Her laa alle feilene som har vaert innom denne fila:
 * dokumentet som hoppet naar klypet slapp, sida som bare kunne blas til
 * hoeyre, zoomen som forankret seg i midten. De kom alle av det samme — at
 * BILDET og LAYOUTEN hadde hver sin stoerrelse mens man zoomet, og at de ble
 * forsonet etterpaa.
 *
 * Naa er det én stoerrelse. `.dokflate__boks` er like stor som det man ser,
 * alltid, og `.dokflate__indre` ligger absolutt plassert i den og skaleres
 * fra sitt eget hjoerne (`transform-origin: 0 0`). Forankringen gjoeres med
 * RULLEPOSISJONEN, slik en rullevisning gjoer det: punktet under fingeren
 * holdes i ro ved aa flytte flata, ikke ved aa flytte innholdet. Da stemmer
 * rullegrensene i hvert eneste bilde, og det finnes ikke noe oeyeblikk aa
 * forsone.
 *
 * TEGNINGEN ER BARE OPPLOESNING. `tegnet` er maalestokken punktgrafikken
 * ligger i; `visning` er den man ser. Byttes punktgrafikken, er den nye
 * visningsstoerrelsen den samme som den gamle — bare skarpere. Derfor kan
 * den byttes uten at noe flytter seg, og derfor tegnes det ikke om naar man
 * zoomer UT: det som alt staar der er tettere enn det trenger aa vaere.
 *
 * TOPPSTRIPA VENTER. Et trykk kan vise seg aa vaere det foerste av to, og
 * stripa skal ikke blinke mens man venter paa det andre. Derfor er det én
 * forsinkelse paa stripa — og ingen paa zoomen.
 */

const MIN_ZOOM = 1;
const MAKS_ZOOM = 6;
/*
 * Hvor langt inn et dobbelttrykk gaar.
 *
 * 2,5 og ikke `MAKS_ZOOM`: trykket skal gjoere en dagsplan lesbar i ett
 * grep, ikke vise fire bokstaver. Vil man naermere, er klypet der.
 */
const DOBBEL_ZOOM = 2.5;
/** Dobbelttrykk: to trykk naermere enn dette i tid og rom. */
const DOBBEL_MS = 280;
const DOBBEL_PX = 40;
/** Et trykk er kort og staar stille. Alt annet er en rulling eller et klyp. */
const TRYKK_MS = 300;
const TRYKK_PX = 10;

/**
 * Hvor lenge zoomen glir, lest ut av `--t-enter`.
 *
 * Leses den her, foelger vi `prefers-reduced-motion` — der settes tokenet
 * ned — uten aa gjenta tallet i to filer.
 */
function glidMs(): number {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue("--t-enter").trim();
  const n = Number.parseFloat(v);
  if (!Number.isFinite(n)) return 220;
  return v.endsWith("ms") ? n : n * 1000;
}

/**
 * Safaris egen klype-hendelse.
 *
 * Ikke i noen standard, og ikke i `lib.dom`. Den finnes bare paa WebKit —
 * og det er nettopp der vi trenger den: iOS kan tolke to fingre paa en
 * rullbar flate som TO-FINGER-RULLING og aldri sende pekerhendelsene videre.
 * Da ville klypet aldri naadd koden, og dokumentet bare sprettet tilbake.
 *
 * `scale` er maalestokken siden gesten startet, som er akkurat det vi
 * trenger. Pekerveien staar igjen for alt annet enn WebKit.
 */
interface WebKitGest extends Event {
  readonly scale: number;
  readonly clientX: number;
  readonly clientY: number;
}

const klem = (v: number): number => Math.min(MAKS_ZOOM, Math.max(MIN_ZOOM, v));

export function DokumentFlate({
  tittel, meta, url, onClose,
}: {
  readonly tittel: string;
  readonly meta: string;
  readonly url: string | null;
  onClose(): void;
}): JSX.Element {
  const [stripe, setStripe] = useState(true);
  /** Maalestokken sidene TEGNES i. Ikke den man ser. */
  const [tegneZoom, setTegneZoom] = useState(1);
  /** Bredden zoom 1 betyr. Maales av flata, ikke av innholdet. */
  const [basis, setBasis] = useState<number | null>(null);

  /** Hele flata. Baerer den maalte hoeyden paa stripa. */
  const skall = useRef<HTMLDivElement | null>(null);
  const stripeEl = useRef<HTMLDivElement | null>(null);
  const rull = useRef<HTMLDivElement | null>(null);
  const boks = useRef<HTMLDivElement | null>(null);
  const indre = useRef<HTMLDivElement | null>(null);

  /** Maalestokken man SER. Endres uten at React tegner noe om. */
  const visning = useRef(1);
  /** Maalestokken punktgrafikken i treet ligger i. */
  const tegnet = useRef(1);
  /** Maalestokken vi har bestilt en tegning i. */
  const bestilt = useRef(1);
  /**
   * Enhetspiksler per CSS-piksel i punktgrafikken som staar der, maalt ved
   * `tegnet`. Det er dette tallet som avgjoer om en ny tegning ville gjort
   * noen forskjell — ikke maalestokken i seg selv.
   */
  const tetthet = useRef(0);

  /*
   * Flata maales foer foerste side tegnes.
   *
   * Sidene ligger i en absolutt plassert boks, og en slik boks har ingen
   * bredde aa lese av foer den har innhold. Uten dette ville zoom 1 blitt
   * gjettet, og gjetningen ville fulgt med videre i alle maalestokker.
   */
  useLayoutEffect(() => {
    const f = rull.current;
    if (f === null) return;
    const stil = getComputedStyle(f);
    const bredde = f.clientWidth
      - Number.parseFloat(stil.paddingLeft)
      - Number.parseFloat(stil.paddingRight);
    if (bredde > 0) setBasis(bredde);
  }, []);

  /*
   * Dokumentet skal begynne DER STRIPA SLUTTER.
   *
   * Stripa ligger over dokumentet og tar ingen plass — den maa gjoere det,
   * for et dokument som hopper hver gang stripa gaar er verre enn ingen
   * stripe. Plassen maa derfor gis som innrykk i rulleflata, og hoeyden var
   * gjettet til 56 px pluss hakket. Den er 68: to linjer tekst, linjeavstand
   * og to ganger `--s-md`. Tolv piksler av dokumentet laa altsaa bak stripa.
   *
   * Her maales den i stedet. `ResizeObserver` holder tallet riktig om
   * skriftstoerrelsen eller hakket endrer seg — iOS melder hakket foerst
   * etter foerste bilde, og ved dreining melder det et nytt.
   *
   * Innrykket STAAR naar stripa skjuler seg. Det er hele grunnen til at
   * stripa ikke tar plass selv.
   */
  useLayoutEffect(() => {
    const vert = skall.current;
    const el = stripeEl.current;
    if (vert === null || el === null) return;
    const maal = (): void => {
      vert.style.setProperty("--dokstripe", `${el.offsetHeight}px`);
    };
    maal();
    const vakt = new ResizeObserver(maal);
    vakt.observe(el);
    return () => { vakt.disconnect(); };
  }, []);

  /**
   * Setter visningsmaalestokken og holder ett punkt i ro.
   *
   * `u` er punktets andel av innholdet, `f` er der det skal staa paa
   * skjermen. Boksen faar sin nye stoerrelse FOER rullingen rettes, slik at
   * grensene allerede er de nye naar vi ruller — ellers klemmes rettingen
   * mot en grense som er i ferd med aa flytte seg.
   */
  const settVisning = useCallback((
    v: number, ux: number, uy: number, fx: number, fy: number,
  ): void => {
    const i = indre.current;
    const b = boks.current;
    const flate = rull.current;
    if (i === null || b === null || flate === null) return;

    visning.current = v;
    const k = v / tegnet.current;
    i.style.transform = k === 1 ? "" : `scale(${k})`;

    const bredde = i.offsetWidth * k;
    const hoeyde = i.offsetHeight * k;
    b.style.width = `${bredde}px`;
    b.style.height = `${hoeyde}px`;

    const r = b.getBoundingClientRect();
    flate.scrollLeft += (r.left + ux * bredde) - fx;
    flate.scrollTop += (r.top + uy * hoeyde) - fy;
  }, []);

  /** Punktets andel av innholdet, fra et punkt paa skjermen. */
  const andel = useCallback((fx: number, fy: number): { ux: number; uy: number } => {
    const b = boks.current;
    if (b === null) return { ux: 0.5, uy: 0 };
    const r = b.getBoundingClientRect();
    return {
      ux: r.width === 0 ? 0.5 : (fx - r.left) / r.width,
      uy: r.height === 0 ? 0 : (fy - r.top) / r.height,
    };
  }, []);

  /**
   * Bestiller ny tegning — men bare naar den faktisk trengs.
   *
   * SPOERSMAALET ER TETTHET, IKKE MAALESTOKK. Sidene tegnes med overskudd,
   * saa punktgrafikken taaler at man zoomer et godt stykke inn uten aa bli
   * uskarp. Saa lenge det som staar der er tett nok for skjermen, skal det
   * ikke roeres: en ny tegning gir da ingenting, og det var nettopp den
   * unoedvendige tegningen som gjorde teksten tykk et oeyeblikk hver gang
   * man zoomet.
   *
   * Vi tegner likevel om naar tettheten faller under skjermens, og naar den
   * er latterlig mye hoeyere enn den trenger aa vaere: et lerret i seks
   * gangers maalestokk ligger paa titalls megabyte, og telefonen tar livet
   * av appen foer den gir fra seg minne.
   */
  const kanskjeTegne = useCallback((): void => {
    const d = tetthet.current;
    if (d === 0) return;
    const v = visning.current;
    // Tettheten slik den er NAA, med dagens visningsmaalestokk.
    const naa = (d * tegnet.current) / v;
    const skjerm = Math.min(window.devicePixelRatio || 1, 3);
    if (naa >= skjerm * 0.75 && naa <= skjerm * 3) return;
    if (Math.abs(v - bestilt.current) < 0.001) return;
    bestilt.current = v;
    setTegneZoom(v);
  }, []);

  /**
   * Kalles naar de nye sidene staar i treet.
   *
   * Stoerrelsen paa skjermen skal vaere NOEYAKTIG den samme foer og etter:
   * `offsetWidth * k` er `basis * visning` i begge tilfeller. Derfor roeres
   * ikke rullingen her — byttet skal ikke kunne ses.
   */
  const etterTegning = useCallback((): void => {
    tegnet.current = bestilt.current;
    const i = indre.current;
    const b = boks.current;
    if (i === null || b === null) return;

    // Hva tegningen faktisk ble. Taket paa lerretsarealet kan ha senket
    // tettheten, og da er det den senkede verdien som gjelder videre.
    const lerret = i.querySelector("canvas");
    if (lerret !== null) {
      const css = Number.parseFloat(lerret.style.width);
      if (css > 0) tetthet.current = lerret.width / css;
    }

    const k = visning.current / tegnet.current;
    i.style.transform = k === 1 ? "" : `scale(${k})`;
    b.style.width = `${i.offsetWidth * k}px`;
    b.style.height = `${i.offsetHeight * k}px`;
  }, []);

  /* --- Glid ------------------------------------------------------------ */

  const bilde = useRef<number | null>(null);
  const nettet = useRef<number | null>(null);
  /** Avslutningen til et glid som er i gang. */
  const sluttGlid = useRef<(() => void) | null>(null);

  const stoppGlid = useCallback((): void => {
    if (bilde.current !== null) cancelAnimationFrame(bilde.current);
    if (nettet.current !== null) window.clearTimeout(nettet.current);
    bilde.current = null;
    nettet.current = null;
    sluttGlid.current = null;
  }, []);

  /**
   * Lar et paagaaende glid komme i havn foer noe annet overtar.
   *
   * Under et glid ligger stillingen i en `transform`, og boksen staar
   * fortsatt i den gamle stoerrelsen. Begynner en ny gest der, ville den
   * forankret seg mot en boks som ikke er den man ser. Derfor LANDES glidet
   * foerst — det er ett bilde, og det er det riktige bildet.
   */
  const landGlid = useCallback((): void => {
    sluttGlid.current?.();
  }, []);

  /**
   * Zoomer til `til` over tid, forankret i punktet man trykket.
   *
   * Klypet har fingrene til aa vise hvor det baerer. Et trykk har ingenting,
   * og da maa bevegelsen selv fortelle at det er den samme sida man ser
   * naermere paa.
   *
   * GLIDET ROERER IKKE RULLINGEN. Her laa buen: foerste utgave satte baade
   * maalestokk, boksstoerrelse OG rulleposisjon i hvert bilde. Rullingen paa
   * iOS ligger paa en egen traad — den blir ikke noedvendigvis med i det
   * samme bildet som skaleringen — og da skled de to fra hverandre og fant
   * sammen igjen, bilde for bilde. Det ser ut som to bevegelser som avloeser
   * hverandre, og banen blir en bue i stedet for en zoom rett inn.
   *
   * Naa regnes SLUTTSTILLINGEN ut foerst, ved aa sette den og maale hva
   * rullingen faktisk ble — da er klemming mot rullegrensene med i tallet.
   * Saa stilles alt tilbake, og bare `transform` animeres: avstanden mellom
   * de to rulleposisjonene legges inn som en forskyvning. Da er det én
   * eneste egenskap som endrer seg, den ligger paa GPU-en, og punktet under
   * fingeren staar matematisk stille gjennom hele glidet — forskyvningen og
   * maalestokken foelger samme kurve, saa de to stillingene veies mot
   * hverandre uten at noe kan komme i utakt.
   */
  const glid = useCallback((til: number, fx: number, fy: number): void => {
    landGlid();
    stoppGlid();
    const i = indre.current;
    const b = boks.current;
    const flate = rull.current;
    if (i === null || b === null || flate === null) return;

    const { ux, uy } = andel(fx, fy);
    const fra = visning.current;
    const fullfoer = (): void => {
      stoppGlid();
      settVisning(til, ux, uy, fx, fy);
      kanskjeTegne();
    };
    const ms = glidMs();
    if (ms <= 0 || Math.abs(til - fra) < 0.001) {
      fullfoer();
      return;
    }

    const k0 = fra / tegnet.current;
    const k1 = til / tegnet.current;
    const sx0 = flate.scrollLeft;
    const sy0 = flate.scrollTop;

    // Sett sluttstillingen, les av hva den ble, og still tilbake. Alt skjer
    // i samme omgang, saa ingenting av det naar skjermen.
    settVisning(til, ux, uy, fx, fy);
    const sx1 = flate.scrollLeft;
    const sy1 = flate.scrollTop;
    visning.current = fra;
    i.style.transform = `scale(${k0})`;
    b.style.width = `${i.offsetWidth * k0}px`;
    b.style.height = `${i.offsetHeight * k0}px`;
    flate.scrollLeft = sx0;
    flate.scrollTop = sy0;

    const start = performance.now();
    const steg = (naa: number): void => {
      const t = Math.min(1, (naa - start) / ms);
      // Kubisk utgang, som `--ease-out`: raskt i gang, mykt i maal.
      const e = 1 - (1 - t) ** 3;
      i.style.transform = `translate(${(sx0 - sx1) * e}px, ${(sy0 - sy1) * e}px)`
        + ` scale(${k0 + (k1 - k0) * e})`;
      if (t < 1) bilde.current = requestAnimationFrame(steg);
      else fullfoer();
    };
    sluttGlid.current = fullfoer;
    bilde.current = requestAnimationFrame(steg);
    /*
     * Sikkerhetsnett, og det maa ligge GODT utenfor.
     *
     * Uten bilder — fanen i bakgrunnen, stroemsparing — skal zoomen likevel
     * komme fram. Men ligger nettet tett paa, kapper det et glid som bare er
     * litt forsinket, og da ser man nettopp det avbruddet man proever aa
     * unngaa.
     */
    nettet.current = window.setTimeout(fullfoer, ms * 2 + 300);
  }, [landGlid, stoppGlid, andel, settVisning, kanskjeTegne]);

  /* --- Gester ---------------------------------------------------------- */

  /** Aktive fingre. To betyr klyp. */
  const fingre = useRef(new Map<number, { x: number; y: number }>());
  const klyp = useRef<{ avstand: number; fra: number; ux: number; uy: number } | null>(null);
  /** Startpunktet for ett trykk, til aa skille trykk fra rulling. */
  const trykk = useRef<{ x: number; y: number; tid: number } | null>(null);
  /**
   * Trykket som venter paa aa faa vite om det var alene.
   *
   * Stripa maa vente paa det andre trykket. Slo den med én gang og ble
   * angret etterpaa, blinket den ved hvert dobbelttrykk — og et blink
   * midt i en zoom ser ut som en feil.
   */
  const venter = useRef<{ x: number; y: number; tid: number } | null>(null);
  const ventetid = useRef<number | null>(null);

  const glemTrykk = useCallback((): void => {
    if (ventetid.current !== null) window.clearTimeout(ventetid.current);
    ventetid.current = null;
    venter.current = null;
  }, []);

  const midtpunkt = (): { x: number; y: number; d: number } => {
    const [a, b] = [...fingre.current.values()];
    if (a === undefined || b === undefined) return { x: 0, y: 0, d: 0 };
    return {
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
      d: Math.hypot(a.x - b.x, a.y - b.y),
    };
  };

  const startKlyp = useCallback((fx: number, fy: number, avstand: number): void => {
    landGlid();
    glemTrykk();
    trykk.current = null;
    const { ux, uy } = andel(fx, fy);
    klyp.current = { avstand, fra: visning.current, ux, uy };
  }, [landGlid, glemTrykk, andel]);

  const ned = useCallback((e: React.PointerEvent): void => {
    fingre.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (fingre.current.size === 1) {
      trykk.current = { x: e.clientX, y: e.clientY, tid: Date.now() };
    } else if (fingre.current.size === 2) {
      const m = midtpunkt();
      startKlyp(m.x, m.y, m.d);
    }
  }, [startKlyp]);

  const beveg = useCallback((e: React.PointerEvent): void => {
    if (!fingre.current.has(e.pointerId)) return;
    fingre.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (trykk.current !== null) {
      const d = Math.hypot(e.clientX - trykk.current.x, e.clientY - trykk.current.y);
      if (d > TRYKK_PX) trykk.current = null;
    }

    const k = klyp.current;
    if (k === null || fingre.current.size !== 2 || k.avstand === 0) return;
    const m = midtpunkt();
    // Midtpunktet foelger fingrene, saa man kan flytte sida mens man klyper.
    settVisning(klem(k.fra * (m.d / k.avstand)), k.ux, k.uy, m.x, m.y);
  }, [settVisning]);

  const opp = useCallback((e: React.PointerEvent): void => {
    fingre.current.delete(e.pointerId);

    if (klyp.current !== null && fingre.current.size < 2) {
      klyp.current = null;
      kanskjeTegne();
      return;
    }

    const t = trykk.current;
    trykk.current = null;
    if (t === null || Date.now() - t.tid >= TRYKK_MS) return;

    const f = venter.current;
    if (f !== null && Math.hypot(t.x - f.x, t.y - f.y) < DOBBEL_PX) {
      // Det andre trykket. Stripa fikk aldri roert seg.
      glemTrykk();
      glid(visning.current > MIN_ZOOM + 0.05 ? MIN_ZOOM : DOBBEL_ZOOM, t.x, t.y);
      return;
    }

    glemTrykk();
    venter.current = { x: t.x, y: t.y, tid: Date.now() };
    ventetid.current = window.setTimeout(() => {
      ventetid.current = null;
      venter.current = null;
      setStripe((v) => !v);
    }, DOBBEL_MS);
  }, [kanskjeTegne, glemTrykk, glid]);

  /*
   * WebKits klype-hendelser. Registreres med `addEventListener` fordi React
   * ikke kjenner dem, og `passive: false` fordi vi maa kunne hindre at
   * Safari gjoer noe eget med gesten.
   */
  useEffect(() => {
    const flate = rull.current;
    if (flate === null) return;
    let fra = 1;
    let fast = { ux: 0.5, uy: 0 };

    const paaStart = (e: Event): void => {
      e.preventDefault();
      const g = e as WebKitGest;
      landGlid();
      glemTrykk();
      // Et klyp er aldri et trykk, og pekerveien skal ikke ogsaa reagere.
      trykk.current = null;
      klyp.current = null;
      fra = visning.current;
      // `clientX/Y` paa en WebKit-gest ER midtpunktet mellom fingrene.
      fast = andel(g.clientX, g.clientY);
    };
    const paaEndring = (e: Event): void => {
      e.preventDefault();
      const g = e as WebKitGest;
      settVisning(klem(fra * g.scale), fast.ux, fast.uy, g.clientX, g.clientY);
    };
    const paaSlutt = (e: Event): void => {
      e.preventDefault();
      kanskjeTegne();
    };

    flate.addEventListener("gesturestart", paaStart, { passive: false });
    flate.addEventListener("gesturechange", paaEndring, { passive: false });
    flate.addEventListener("gestureend", paaSlutt, { passive: false });
    return () => {
      flate.removeEventListener("gesturestart", paaStart);
      flate.removeEventListener("gesturechange", paaEndring);
      flate.removeEventListener("gestureend", paaSlutt);
    };
  }, [landGlid, glemTrykk, andel, settVisning, kanskjeTegne]);

  // Et glid eller et ventende trykk skal ikke vekke en avmontert flate.
  useEffect(() => () => {
    stoppGlid();
    glemTrykk();
  }, [stoppGlid, glemTrykk]);

  return (
    <div className="dokflate" ref={skall}>
      {/* Stripa ligger OVER dokumentet og tones ut, den tar ikke plass. Et
          dokument som hopper naar stripa gaar er verre enn ingen stripe. */}
      <div
        ref={stripeEl}
        className={`dokflate__stripe${stripe ? "" : " dokflate__stripe--skjult"}`}
      >
        <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <span className="dokflate__tittel">{tittel}</span>
          <span className="dokflate__meta mono">{meta}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="dokflate__lukk"
          aria-label="Lukk dokumentet"
        >
          ✕
        </button>
      </div>

      <div
        className="dokflate__rull"
        ref={rull}
        onPointerDown={ned}
        onPointerMove={beveg}
        onPointerUp={opp}
        onPointerCancel={opp}
      >
        {url === null ? (
          <span className="pdf__feil mono">Fila er ikke lastet ned.</span>
        ) : basis === null ? null : (
          <div className="dokflate__boks" ref={boks}>
            <div className="dokflate__indre" ref={indre}>
              {/*
                Tegnes tettere enn skjermen trenger. Da kan de foerste
                gangene man zoomer gjoeres av CSS alene, og teksten holder
                seg skarp uten at dokumentet maa tegnes om.
              */}
              <Pdf
                url={url}
                zoom={tegneZoom}
                basis={basis}
                overskudd={2}
                onFerdig={etterTegning}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
